import React, { useCallback } from 'react'
import AsyncSelect from 'react-select/async'
import { api } from '../services/api'
import { useDebouncedCallback } from '../hooks'
import selectStyles from '../styles/react-select.module.scss'
import { Season } from '../types/forms'

// Option type extends Season with react-select required fields
interface SeasonOption extends Season {
  value: string
  label: string
}

export interface SeasonSelectProps {
  value: Season | Season[] | null
  onChange: (season: Season | Season[] | null) => void
  isMulti?: boolean
  isClearable?: boolean
  placeholder?: string
  className?: string
  isDisabled?: boolean
  isModified?: boolean
}

// Convert Season to react-select option (exported for use in forms)
export const toOption = (s: Season): SeasonOption => ({ ...s, value: s.tc_id, label: `${s.name} (${s.year})` })

const SeasonSelect: React.FC<SeasonSelectProps> = ({
  value,
  onChange,
  isMulti = false,
  isClearable = true,
  placeholder = 'Search seasons...',
  className = '',
  isDisabled = false,
  isModified = false
}) => {
  const fetchSeasonsData = useCallback(async (inputValue: string): Promise<SeasonOption[]> => {
    if (inputValue.length < 2) return []

    try {
      const response = await api.get('/seasons', { name: inputValue, limit: 50 })
      const rawSeasons = response?.seasons || []

      // Transform API response to Season format
      const seasons: Season[] = rawSeasons.map((item: any) => ({
        tc_id: item.tc_season_id,
        ext_id: item.ext_season_id,
        name: item.tc_name || item.ext_name || 'Unknown Season',
        year: item.tc_year || item.ext_year || 0,
        complex: item.complex || undefined
      }))

      return seasons.map(toOption)
    } catch (error) {
      console.error('Failed to load seasons:', error)
      return []
    }
  }, [])

  const loadSeasons = useDebouncedCallback(fetchSeasonsData, 300)

  // Convert value to option format
  const selectValue = value
    ? (isMulti ? (value as Season[]).map(toOption) : toOption(value as Season))
    : null

  return (
    <div className={isModified ? selectStyles.modified : ''}>
      <AsyncSelect<SeasonOption, typeof isMulti>
        value={selectValue}
        onChange={onChange as any}
        loadOptions={loadSeasons}
        defaultOptions={false}
        openMenuOnFocus={false}
        openMenuOnClick={false}
        isMulti={isMulti}
        isClearable={isClearable}
        isDisabled={isDisabled}
        placeholder={placeholder}
        className={`${selectStyles.reactSelect} ${className}`}
        classNamePrefix="react-select"
        cacheOptions
        getOptionValue={(option) => option.tc_id}
        getOptionLabel={(option) => `${option.name} (${option.year})`}
        noOptionsMessage={({ inputValue }) =>
          inputValue.length < 2
            ? "Type at least 2 characters to search..."
            : "No seasons found"
        }
      />
    </div>
  )
}

export default SeasonSelect
